/**
 * Global idle detector — tracks user activity (mouse, keyboard, touch)
 * and provides an adaptive multiplier for polling intervals.
 *
 * Inspired by CodyChat's innactiveControl: when user is idle,
 * polling intervals increase gradually (up to 3x), reducing DB load.
 *
 * Active user:  multiplier = 1  (normal intervals)
 * Idle 2min:    multiplier = 2  (double intervals)
 * Idle 4min+:   multiplier = 3  (triple intervals — max)
 */

const IDLE_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes
const MAX_MULTIPLIER = 3;

let lastActivity = Date.now();
let currentMultiplier = 1;
let initialized = false;

function onActivity() {
  lastActivity = Date.now();
  currentMultiplier = 1;
}

function initIfNeeded() {
  if (initialized || typeof document === "undefined") return;
  initialized = true;

  const events = ["mousemove", "keydown", "touchstart", "scroll", "click", "pointerdown"];
  for (const evt of events) {
    document.addEventListener(evt, onActivity, { passive: true, capture: true });
  }

  // Recalculate multiplier every 30s
  setInterval(() => {
    const elapsed = Date.now() - lastActivity;
    if (elapsed > IDLE_THRESHOLD_MS) {
      currentMultiplier = Math.min(
        MAX_MULTIPLIER,
        1 + Math.floor(elapsed / IDLE_THRESHOLD_MS)
      );
    } else {
      currentMultiplier = 1;
    }
  }, 30_000);
}

/** Returns 1 when active, 2-3 when idle */
export function getIdleMultiplier(): number {
  initIfNeeded();
  return currentMultiplier;
}

/** Returns baseMs * idleMultiplier — use for adaptive polling */
export function getAdaptiveInterval(baseMs: number): number {
  return baseMs * getIdleMultiplier();
}

/** Returns true if user has been idle for at least the given ms */
export function isUserIdle(thresholdMs = IDLE_THRESHOLD_MS): boolean {
  initIfNeeded();
  return Date.now() - lastActivity > thresholdMs;
}
